import React, { useState, useEffect } from 'react';
import { integrationAttemptService, IntegrationMessageAttemptItem } from './integrationAttemptService';
import { integrationPayloadService, IntegrationMessagePayloadItem } from './integrationPayloadService';
import { integrationErrorService, IntegrationErrorItem } from './integrationErrorService';
import { useTableSort, SortableTh } from '../../shared/useTableSort';

export const IntegrationAttemptsPage: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'attempts' | 'payloads' | 'errors'>('attempts');

  // Attempts State
  const [attempts, setAttempts] = useState<IntegrationMessageAttemptItem[]>([]);
  const [loadingAttempts, setLoadingAttempts] = useState<boolean>(true);
  const [filterStatus, setFilterStatus] = useState<string>('ALL');
  const [searchMsgId, setSearchMsgId] = useState<string>('');

  // Attempt Form
  const [formMsgId, setFormMsgId] = useState<string>('');
  const [formStatus, setFormStatus] = useState<string>('SUCCESS');
  const [formHttpStatus, setFormHttpStatus] = useState<number>(200);
  const [formDuration, setFormDuration] = useState<number>(120);
  const [formResponse, setFormResponse] = useState<string>('Delivered successfully');
  const [submittingAttempt, setSubmittingAttempt] = useState<boolean>(false);
  const [attemptFeedback, setAttemptFeedback] = useState<string | null>(null);

  // Payloads State
  const [payloads, setPayloads] = useState<IntegrationMessagePayloadItem[]>([]);
  const [loadingPayloads, setLoadingPayloads] = useState<boolean>(false);
  const [payloadFilterType, setPayloadFilterType] = useState<string>('ALL');

  // Payload Form
  const [payloadMsgId, setPayloadMsgId] = useState<string>('');
  const [payloadType, setPayloadType] = useState<string>('REQUEST');
  const [rawContent, setRawContent] = useState<string>('{"flightNumber": "TK1984", "status": "BOARDING"}');
  const [storageRef, setStorageRef] = useState<string>('s3://safetycore-vault/2026/10/msg-123.json');
  const [compression, setCompression] = useState<string>('GZIP');
  const [encryptionRef, setEncryptionRef] = useState<string>('kms://arn:aws:kms:eu-central-1:key-456');
  const [submittingPayload, setSubmittingPayload] = useState<boolean>(false);
  const [payloadFeedback, setPayloadFeedback] = useState<string | null>(null);

  // Errors State
  const [errors, setErrors] = useState<IntegrationErrorItem[]>([]);
  const [loadingErrors, setLoadingErrors] = useState<boolean>(false);
  const [errorTypeFilter, setErrorTypeFilter] = useState<string>('ALL');
  const [retryFilter, setRetryFilter] = useState<string>('ALL');

  // Error Form
  const [errMessageId, setErrMessageId] = useState<string>('');
  const [errCode, setErrCode] = useState<string>('NET_TIMEOUT');
  const [errType, setErrType] = useState<string>('TIMEOUT');
  const [errMessage, setErrMessage] = useState<string>('Connection timed out after 30000ms');
  const [submittingError, setSubmittingError] = useState<boolean>(false);
  const [errorFeedback, setErrorFeedback] = useState<string | null>(null);

  const loadAttempts = async () => {
    try {
      setLoadingAttempts(true);
      const data = await integrationAttemptService.getAttempts(100);
      setAttempts(data);
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoadingAttempts(false);
    }
  };

  const loadPayloads = async () => {
    try {
      setLoadingPayloads(true);
      const data = await integrationPayloadService.getPayloads(100);
      setPayloads(data);
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoadingPayloads(false);
    }
  };

  const loadErrors = async () => {
    try {
      setLoadingErrors(true);
      const data = await integrationErrorService.getErrors({ limit: 100 });
      setErrors(data);
    } catch (err: any) {
      console.error(err);
    } finally {
      setLoadingErrors(false);
    }
  };

  useEffect(() => {
    loadAttempts();
    loadPayloads();
    loadErrors();
  }, []);

  const handleRecordAttempt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formMsgId.trim()) return;

    try {
      setSubmittingAttempt(true);
      setAttemptFeedback(null);
      await integrationAttemptService.recordAttempt(formMsgId.trim(), {
        status: formStatus,
        httpStatus: formHttpStatus || null,
        durationMs: formDuration || null,
        responseMessage: formResponse || null
      });
      setAttemptFeedback('Attempt successfully recorded!');
      await loadAttempts();
    } catch (err: any) {
      setAttemptFeedback(`Failed to record attempt: ${err.message}`);
    } finally {
      setSubmittingAttempt(false);
    }
  };

  const handleRecordPayload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!payloadMsgId.trim()) return;

    try {
      setSubmittingPayload(true);
      setPayloadFeedback(null);
      await integrationPayloadService.recordPayload(payloadMsgId.trim(), {
        payloadType,
        rawPayloadContent: rawContent,
        objectStorageRef: storageRef || undefined,
        compression,
        encryptionKeyRef: encryptionRef || undefined
      });
      setPayloadFeedback('Payload reference successfully recorded!');
      await loadPayloads();
    } catch (err: any) {
      setPayloadFeedback(`Failed to record payload: ${err.message}`);
    } finally {
      setSubmittingPayload(false);
    }
  };

  const handleRecordError = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSubmittingError(true);
      setErrorFeedback(null);
      await integrationErrorService.recordError({
        messageId: errMessageId.trim() || undefined,
        errorCode: errCode,
        errorType: errType,
        errorMessage: errMessage
      });
      setErrorFeedback('Error recorded and classified!');
      await loadErrors();
    } catch (err: any) {
      setErrorFeedback(`Failed to record error: ${err.message}`);
    } finally {
      setSubmittingError(false);
    }
  };

  const retryableErrorsCount = errors.filter((e) => e.retryable).length;

  const { sortedItems: sortedAttempts, sortField: attemptSortField, sortDirection: attemptSortDir, toggleSort: toggleAttemptSort } = useTableSort(attempts);
  const { sortedItems: sortedPayloads, sortField: payloadSortField, sortDirection: payloadSortDir, toggleSort: togglePayloadSort } = useTableSort(payloads);
  const { sortedItems: sortedErrors, sortField: errorSortField, sortDirection: errorSortDir, toggleSort: toggleErrorSort } = useTableSort(errors);

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>
      {/* FlowDesk Standard Header Banner */}
      <div className="page-header-banner">
        <div className="badge">INTEGRATION HUB</div>
        <h1>Integration Pipeline & Telemetry</h1>
        <p>Message delivery attempts, payload vault, and error retry diagnostics</p>
      </div>

      {/* FlowDesk Nav Tabs */}
      <div className="nav-tabs" style={{ marginBottom: '24px' }}>
        <button
          className={`tab-btn ${activeSubTab === 'attempts' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('attempts')}
        >
          Attempts ({attempts.length})
        </button>
        <button
          className={`tab-btn ${activeSubTab === 'payloads' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('payloads')}
        >
          Payloads ({payloads.length})
        </button>
        <button
          className={`tab-btn ${activeSubTab === 'errors' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('errors')}
        >
          Errors & Retry ({errors.length})
        </button>
      </div>

      {activeSubTab === 'attempts' && (
        <>
          {/* Form */}
          <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b', marginTop: 0, marginBottom: '16px' }}>
              Record Message Attempt
            </h2>
            {attemptFeedback && (
              <div style={{ padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '16px', background: '#f0fdf4', color: '#166534' }}>
                {attemptFeedback}
              </div>
            )}
            <form onSubmit={handleRecordAttempt} style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr) auto', gap: '12px', alignItems: 'flex-end' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Message UUID *</label>
                <input
                  type="text"
                  placeholder="e.g. 550e8400..."
                  value={formMsgId}
                  onChange={(e) => setFormMsgId(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Status</label>
                <select
                  value={formStatus}
                  onChange={(e) => setFormStatus(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                >
                  <option value="SUCCESS">SUCCESS</option>
                  <option value="FAILED">FAILED</option>
                  <option value="TIMEOUT">TIMEOUT</option>
                  <option value="RETRIED">RETRIED</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>HTTP Code</label>
                <input
                  type="number"
                  value={formHttpStatus}
                  onChange={(e) => setFormHttpStatus(Number(e.target.value))}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Duration (ms)</label>
                <input
                  type="number"
                  value={formDuration}
                  onChange={(e) => setFormDuration(Number(e.target.value))}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Response Message</label>
                <input
                  type="text"
                  value={formResponse}
                  onChange={(e) => setFormResponse(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                />
              </div>
              <button
                type="submit"
                disabled={submittingAttempt}
                style={{ background: '#0f172a', color: '#fff', border: 'none', borderRadius: '6px', padding: '9px 18px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
              >
                {submittingAttempt ? 'Saving...' : 'Record Attempt'}
              </button>
            </form>
          </div>

          {/* Table */}
          <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                <tr>
                  <SortableTh field="attemptNumber" currentField={attemptSortField} currentDirection={attemptSortDir} onSort={toggleAttemptSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    #
                  </SortableTh>
                  <SortableTh field="messageId" currentField={attemptSortField} currentDirection={attemptSortDir} onSort={toggleAttemptSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Message UUID
                  </SortableTh>
                  <SortableTh field="status" currentField={attemptSortField} currentDirection={attemptSortDir} onSort={toggleAttemptSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Status
                  </SortableTh>
                  <SortableTh field="httpStatus" currentField={attemptSortField} currentDirection={attemptSortDir} onSort={toggleAttemptSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    HTTP
                  </SortableTh>
                  <SortableTh field="durationMs" currentField={attemptSortField} currentDirection={attemptSortDir} onSort={toggleAttemptSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Duration
                  </SortableTh>
                  <SortableTh field="responseMessage" currentField={attemptSortField} currentDirection={attemptSortDir} onSort={toggleAttemptSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Response Message
                  </SortableTh>
                  <SortableTh field="attemptedAt" currentField={attemptSortField} currentDirection={attemptSortDir} onSort={toggleAttemptSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Timestamp
                  </SortableTh>
                </tr>
              </thead>
              <tbody>
                {sortedAttempts.map((att) => (
                  <tr key={att.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 'bold' }}>#{att.attemptNumber}</td>
                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', color: '#0284c7' }}>{att.messageId}</td>
                    <td style={{ padding: '12px 16px' }}>{att.status}</td>
                    <td style={{ padding: '12px 16px' }}>{att.httpStatus || '-'}</td>
                    <td style={{ padding: '12px 16px' }}>{att.durationMs ? `${att.durationMs}ms` : '-'}</td>
                    <td style={{ padding: '12px 16px' }}>{att.responseMessage || '-'}</td>
                    <td style={{ padding: '12px 16px', color: '#64748b' }}>{new Date(att.attemptedAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeSubTab === 'payloads' && (
        <>
          <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b', marginTop: 0, marginBottom: '16px' }}>
              Record Message Payload (SHA-256 Hash & Storage Reference)
            </h2>
            {payloadFeedback && (
              <div style={{ padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '16px', background: '#f0fdf4', color: '#166534' }}>
                {payloadFeedback}
              </div>
            )}
            <form onSubmit={handleRecordPayload} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: '12px', alignItems: 'flex-end' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Message UUID *</label>
                <input
                  type="text"
                  placeholder="e.g. 550e8400..."
                  value={payloadMsgId}
                  onChange={(e) => setPayloadMsgId(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Payload Type</label>
                <select
                  value={payloadType}
                  onChange={(e) => setPayloadType(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                >
                  <option value="REQUEST">REQUEST</option>
                  <option value="RESPONSE">RESPONSE</option>
                  <option value="ORIGINAL">ORIGINAL</option>
                  <option value="TRANSFORMED">TRANSFORMED</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Object Storage Ref</label>
                <input
                  type="text"
                  value={storageRef}
                  onChange={(e) => setStorageRef(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Payload Content (for SHA-256)</label>
                <input
                  type="text"
                  value={rawContent}
                  onChange={(e) => setRawContent(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                />
              </div>
              <button
                type="submit"
                disabled={submittingPayload}
                style={{ background: '#0284c7', color: '#fff', border: 'none', borderRadius: '6px', padding: '9px 18px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
              >
                {submittingPayload ? 'Writing...' : 'Save Payload'}
              </button>
            </form>
          </div>

          <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                <tr>
                  <SortableTh field="payloadType" currentField={payloadSortField} currentDirection={payloadSortDir} onSort={togglePayloadSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Type
                  </SortableTh>
                  <SortableTh field="messageId" currentField={payloadSortField} currentDirection={payloadSortDir} onSort={togglePayloadSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Message UUID
                  </SortableTh>
                  <SortableTh field="payloadHash" currentField={payloadSortField} currentDirection={payloadSortDir} onSort={togglePayloadSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    SHA-256 Hash
                  </SortableTh>
                  <SortableTh field="objectStorageRef" currentField={payloadSortField} currentDirection={payloadSortDir} onSort={togglePayloadSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Object Storage Reference
                  </SortableTh>
                  <SortableTh field="compression" currentField={payloadSortField} currentDirection={payloadSortDir} onSort={togglePayloadSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Compression
                  </SortableTh>
                  <SortableTh field="createdAt" currentField={payloadSortField} currentDirection={payloadSortDir} onSort={togglePayloadSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Created At
                  </SortableTh>
                </tr>
              </thead>
              <tbody>
                {sortedPayloads.map((p) => (
                  <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 'bold' }}>{p.payloadType}</td>
                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', color: '#0284c7' }}>{p.messageId}</td>
                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', fontSize: '11px', color: '#475569' }}>
                      {p.payloadHash?.slice(0, 16)}...
                    </td>
                    <td style={{ padding: '12px 16px', color: '#334155' }}>{p.objectStorageRef || '-'}</td>
                    <td style={{ padding: '12px 16px' }}>{p.compression || 'NONE'}</td>
                    <td style={{ padding: '12px 16px', color: '#64748b' }}>{new Date(p.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {activeSubTab === 'errors' && (
        <>
          {/* KPI */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '24px' }}>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '4px' }}>Total Integration Errors</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0f172a' }}>{errors.length}</div>
            </div>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '4px' }}>Retryable Errors (Rule 20)</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#0284c7' }}>{retryableErrorsCount}</div>
            </div>
            <div style={{ background: '#fff', padding: '16px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '4px' }}>Deterministic / Non-Retryable</div>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc2626' }}>{errors.length - retryableErrorsCount}</div>
            </div>
          </div>

          {/* Form */}
          <div style={{ background: '#fff', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: '24px' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 'bold', color: '#1e293b', marginTop: 0, marginBottom: '16px' }}>
              Classify & Record Error (Rule 20 Enforced)
            </h2>
            {errorFeedback && (
              <div style={{ padding: '10px 14px', borderRadius: '6px', fontSize: '13px', marginBottom: '16px', background: '#f0fdf4', color: '#166534' }}>
                {errorFeedback}
              </div>
            )}
            <form onSubmit={handleRecordError} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr) auto', gap: '12px', alignItems: 'flex-end' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Message UUID (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g. 550e8400..."
                  value={errMessageId}
                  onChange={(e) => setErrMessageId(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Error Type *</label>
                <select
                  value={errType}
                  onChange={(e) => setErrType(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                >
                  <option value="TIMEOUT">TIMEOUT (Retryable)</option>
                  <option value="NETWORK">NETWORK (Retryable)</option>
                  <option value="RATE_LIMIT">RATE_LIMIT (Retryable)</option>
                  <option value="AUTHENTICATION">AUTHENTICATION (Non-Retryable)</option>
                  <option value="AUTHORIZATION">AUTHORIZATION (Non-Retryable)</option>
                  <option value="SCHEMA">SCHEMA (Non-Retryable)</option>
                  <option value="MAPPING">MAPPING (Non-Retryable)</option>
                  <option value="BUSINESS_VALIDATION">BUSINESS_VALIDATION (Non-Retryable)</option>
                  <option value="DUPLICATE">DUPLICATE (Non-Retryable)</option>
                  <option value="CONFLICT">CONFLICT (Non-Retryable)</option>
                  <option value="UNKNOWN">UNKNOWN (Non-Retryable)</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Error Code *</label>
                <input
                  type="text"
                  value={errCode}
                  onChange={(e) => setErrCode(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                  required
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#475569', marginBottom: '4px' }}>Error Message</label>
                <input
                  type="text"
                  value={errMessage}
                  onChange={(e) => setErrMessage(e.target.value)}
                  style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '13px' }}
                />
              </div>
              <button
                type="submit"
                disabled={submittingError}
                style={{ background: '#dc2626', color: '#fff', border: 'none', borderRadius: '6px', padding: '9px 18px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
              >
                {submittingError ? 'Logging...' : 'Log Error'}
              </button>
            </form>
          </div>

          {/* Table */}
          <div style={{ background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#475569' }}>
                <tr>
                  <SortableTh field="errorCode" currentField={errorSortField} currentDirection={errorSortDir} onSort={toggleErrorSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Error Code
                  </SortableTh>
                  <SortableTh field="errorType" currentField={errorSortField} currentDirection={errorSortDir} onSort={toggleErrorSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Type
                  </SortableTh>
                  <SortableTh field="retryable" currentField={errorSortField} currentDirection={errorSortDir} onSort={toggleErrorSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Retryable?
                  </SortableTh>
                  <SortableTh field="messageId" currentField={errorSortField} currentDirection={errorSortDir} onSort={toggleErrorSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Message UUID
                  </SortableTh>
                  <SortableTh field="errorMessage" currentField={errorSortField} currentDirection={errorSortDir} onSort={toggleErrorSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Error Message
                  </SortableTh>
                  <SortableTh field="createdAt" currentField={errorSortField} currentDirection={errorSortDir} onSort={toggleErrorSort} style={{ padding: '12px 16px', fontWeight: 600 }}>
                    Logged At
                  </SortableTh>
                </tr>
              </thead>
              <tbody>
                {sortedErrors.map((err) => (
                  <tr key={err.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 'bold', fontFamily: 'monospace' }}>{err.errorCode}</td>
                    <td style={{ padding: '12px 16px' }}>{err.errorType}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: 600,
                        background: err.retryable ? '#dbeafe' : '#fee2e2',
                        color: err.retryable ? '#1e40af' : '#991b1b',
                        border: `1px solid ${err.retryable ? '#93c5fd' : '#fca5a5'}`
                      }}>
                        {err.retryable ? '✓ RETRYABLE' : '✗ NON-RETRYABLE'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', fontFamily: 'monospace', color: '#0284c7' }}>{err.messageId || 'GLOBAL'}</td>
                    <td style={{ padding: '12px 16px', color: '#334155' }}>{err.errorMessage}</td>
                    <td style={{ padding: '12px 16px', color: '#64748b' }}>{new Date(err.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};
